import React from "react";
import { motion } from "framer-motion";
import { Star, Quote } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";

const TrustpilotStars = ({ count = 5 }) => (
  <div className="flex items-center gap-0.5">
    {Array.from({ length: 5 }).map((_, i) => (
      <div
        key={i}
        className="w-6 h-6 md:w-7 md:h-7 flex items-center justify-center"
        style={{ background: i < count ? "#00b67a" : "#191919" }}
      >
        <Star size={14} className="text-white fill-white" />
      </div>
    ))}
  </div>
);

const Testimonials = () => {
  const { t } = useLang();

  return (
    <section
      id="testimonials"
      data-testid="testimonials-section"
      className="py-20 md:py-32 border-t border-white/10"
    >
      <div className="container-hk">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
          <div>
            <div className="text-[10px] md:text-xs tracking-[0.35em] text-[var(--hk-red)] font-bold mb-3">
              // 005 · {t.testimonials.overline}
            </div>
            <h2 className="font-display text-4xl md:text-6xl lg:text-7xl uppercase leading-[0.95] tracking-tight">
              {t.testimonials.title}
            </h2>
          </div>
          <p className="text-sm text-white/60 max-w-sm">
            {t.testimonials.subtitle}
          </p>
        </div>

        {/* Trustpilot badge */}
        <div
          data-testid="trustpilot-badge"
          className="flex flex-col md:flex-row md:items-center gap-5 md:gap-8 p-6 md:p-8 border border-white/10 bg-black mb-12"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center" style={{ background: "#00b67a" }}>
              <Star size={20} className="text-white fill-white" />
            </div>
            <div className="font-display text-2xl md:text-3xl tracking-tight">
              Trust<span className="text-[#00b67a]">pilot</span>
            </div>
          </div>
          <div className="hidden md:block h-10 w-px bg-white/10" />
          <TrustpilotStars count={5} />
          <div className="flex-1 flex flex-col md:flex-row md:items-baseline gap-2 md:gap-4">
            <div className="font-display text-3xl md:text-4xl">{t.testimonials.score}</div>
            <div className="text-xs md:text-sm text-white/60">
              {t.testimonials.trustpilot} · {t.testimonials.reviews}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {t.testimonials.items.map((r, i) => (
            <motion.div
              key={r.name}
              data-testid={`testimonial-${i}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ delay: i * 0.06, duration: 0.6 }}
              className="relative bg-black border border-white/10 p-6 md:p-8 hover:border-[var(--hk-red)] transition-colors"
            >
              <Quote
                size={28}
                className="absolute top-6 right-6 text-[var(--hk-red)]/30"
              />
              <div className="flex items-center gap-0.5 mb-4">
                {Array.from({ length: r.stars }).map((_, s) => (
                  <div
                    key={s}
                    className="w-5 h-5 flex items-center justify-center"
                    style={{ background: "#00b67a" }}
                  >
                    <Star size={11} className="text-white fill-white" />
                  </div>
                ))}
              </div>
              <p className="text-sm md:text-[15px] text-white/80 leading-relaxed mb-6">
                "{r.text}"
              </p>
              <div className="pt-4 border-t border-white/10">
                <div className="font-semibold text-sm">{r.name}</div>
                <div className="text-xs text-white/50 mt-0.5">{r.country}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
